/**
 * Shared helper functions for order calculations
 * Used by order.js, tableOrder.js, and onlineOrder.js modules
 */

/**
 * Resolves the tax rate value for an item
 * @param {Object} taxItem - The item to resolve tax rate for
 * @param {Array} taxRates - Available tax rates from store
 * @returns {number} The tax rate percentage
 */
export function resolveTaxRateValue(taxItem, taxRates) {
  if (
    typeof taxItem.tax_rate_value === "number" &&
    !Number.isNaN(taxItem.tax_rate_value)
  ) {
    return taxItem.tax_rate_value;
  }

  const taxRateId = taxItem.product?.tax_rates?.[0];
  if (!taxRateId) {
    return 0;
  }

  const matchedRate = taxRates.find((tr) => tr.id === taxRateId);
  return matchedRate ? matchedRate.rate : 0;
}

/**
 * Gets active modifiers from an item
 * @param {Object} item - The item
 * @param {boolean} useCartItems - Whether using cart items or regular items
 * @returns {Array} Array of modifiers
 */
export function getActiveModifiers(item, useCartItems) {
  return useCartItems ? item.selectedModifiers || [] : item.modifiers || [];
}

/**
 * Normalizes modifiers to consistent structure
 * @param {Array} mods - Modifiers to normalize
 * @returns {Array} Normalized modifiers with price property
 */
export function normalizeModifiers(mods) {
  return mods.map((mod) => ({ price: mod.price || 0 }));
}

/**
 * Ensures item has a local_id for tracking
 * @param {Object} item - The item
 * @returns {string} The local_id
 */
export function ensureLocalId(item) {
  if (item.local_id) {
    return item.local_id;
  }
  if (item.cartId) {
    item.local_id = item.cartId;
    return item.local_id;
  }
  const generatedId = `${item.id}-${Date.now()}`;
  item.local_id = generatedId;
  return generatedId;
}

/**
 * Creates or finds a synthetic tax rate from tax_rate_value
 * @param {number} taxRateValue - The tax rate percentage
 * @param {Array} taxRates - Existing tax rates array (will be mutated)
 * @returns {string} The synthetic tax rate ID
 */
export function ensureSyntheticTaxRate(taxRateValue, taxRates) {
  const syntheticId = `synthetic_${taxRateValue}`;

  let syntheticRate = taxRates.find((tr) => tr.id === syntheticId);

  if (!syntheticRate) {
    syntheticRate = {
      id: syntheticId,
      rate: taxRateValue,
      name: `Tax ${taxRateValue}%`
    };
    taxRates.push(syntheticRate);
  }

  return syntheticId;
}

/**
 * Checks if an item has tax information
 * @param {Object} item - The item to check
 * @returns {boolean} True if item has tax info
 */
export function hasTaxInformation(item) {
  return (
    (typeof item.tax_rate_value === "number" &&
      !Number.isNaN(item.tax_rate_value)) ||
    item.product?.tax_rates?.length > 0 ||
    item.tax_rates?.length > 0
  );
}

/**
 * Resolves product with tax rates for an item
 * Handles cases where product doesn't have tax_rates by using item.tax_rates or creating synthetic rates
 * @param {Object} item - The item
 * @param {Array} taxRates - Available tax rates (will be mutated if synthetic rates are created)
 * @returns {Object|null} Product object with tax_rates, or null if no tax info available
 */
export function resolveProductWithTaxRates(item, taxRates) {
  let productWithTaxRates = item.product;

  if (
    !productWithTaxRates ||
    !productWithTaxRates.tax_rates ||
    productWithTaxRates.tax_rates.length === 0
  ) {
    let itemTaxRates = item.tax_rates || [];

    // If item has tax_rate_value but no tax_rates array, create a synthetic tax rate
    if (
      itemTaxRates.length === 0 &&
      item.tax_rate_value != null &&
      !Number.isNaN(item.tax_rate_value) &&
      item.tax_rate_value > 0
    ) {
      const syntheticId = ensureSyntheticTaxRate(item.tax_rate_value, taxRates);
      itemTaxRates = [syntheticId];
    }

    if (itemTaxRates.length > 0) {
      productWithTaxRates = {
        id: item.id,
        tax_rates: itemTaxRates
      };
    } else {
      return null;
    }
  }

  return productWithTaxRates;
}

/**
 * Calculates subtotal for items
 * @param {Array} items - The items to calculate subtotal for
 * @param {boolean} useCartItems - Whether using cart items
 * @returns {number} The subtotal
 */
export function calculateSubtotal(items, useCartItems) {
  return (
    items.reduce((sum, item) => {
      const modifiers = getActiveModifiers(item, useCartItems);
      const modTotal = modifiers.reduce(
        (mSum, mod) => mSum + (mod.price || 0),
        0
      );
      return sum + (item.price + modTotal) * item.quantity;
    }, 0) || 0
  );
}

/**
 * Calculates grand total based on subtotal, tax, and inclusive tax setting
 * @param {number} subtotal - The subtotal amount
 * @param {number} totalTax - The total tax amount
 * @param {boolean} isTaxInclusive - Whether tax is inclusive
 * @returns {number} The grand total
 */
export function calculateGrandTotal(subtotal, totalTax, isTaxInclusive) {
  if (isTaxInclusive) {
    return subtotal;
  }
  return subtotal + totalTax;
}
